"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { Bell, X } from "lucide-react";
import type { Announcement, Platform, SiteSettings } from "@/lib/types";
import CommunityPopup from "./community-popup";
import InboxPanel from "./inbox-panel";
import BugReportForm from "./bug-report-form";

interface HomeClientProps {
  platforms: Platform[];
  announcements: Announcement[];
  settings: SiteSettings;
}

export default function HomeClient({
  platforms,
  announcements,
  settings,
}: HomeClientProps) {
  const [inboxOpen, setInboxOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // Defer client-only state (localStorage) to avoid cascading renders
    const timer = setTimeout(() => {
      setMounted(true);
      // Compute unread announcements based on last read timestamp
      const lastRead = localStorage.getItem("bison_inbox_last_read");
      const lastReadTime = lastRead ? parseInt(lastRead, 10) : 0;
      const unread = announcements.filter(
        (a) => new Date(a.createdAt).getTime() > lastReadTime
      ).length;
      setUnreadCount(unread);
    }, 0);
    return () => clearTimeout(timer);
  }, [announcements]);

  const handleInboxOpen = () => {
    setInboxOpen((prev) => {
      const opening = !prev;
      if (opening) {
        setUnreadCount(0);
        localStorage.setItem("bison_inbox_last_read", Date.now().toString());
      }
      return opening;
    });
  };

  return (
    <div
      data-theme={settings.theme || "default"}
      className="min-h-screen flex flex-col bg-zinc-950 text-white overflow-x-hidden relative"
style={{
          background:
            "radial-gradient(ellipse 800px 500px at 30% -10%, rgba(99,102,241,0.25), transparent 60%), radial-gradient(ellipse 600px 400px at 70% 50%, rgba(59,130,246,0.15), transparent 60%), linear-gradient(180deg, #0e0a20 0%, #0a0a15 40%, #0a0a15 100%)",
        }}
    >
      {/* Hidden SEO Content */}
      <div className="seo-hidden-text" aria-hidden="true">
        <h1>Study Bison - The Ultimate Educational Platform</h1>
        <p>
          Welcome to Study Bison, the ultimate free education ecosystem.
          Explore our vast free resources across all platforms. Study Bison is
          dedicated to providing the best learning experience through Next
          Toppers, Physics Wallah and more. Every student loves Study Bison
          because Study Bison provides quality content completely free.
        </p>
        <h2>Why Choose Study Bison?</h2>
        <p>
          Study Bison provides comprehensive materials for every competitive
          exam. Enhance your knowledge with our curated ecosystem of learning
          platforms. Study Bison is the leading platform for competitive
          exams. Join the Study Bison community today!
        </p>
        <h3>Partners &amp; Platforms in the Study Bison Ecosystem</h3>
        <ul>
          {platforms.map((p) => (
            <li key={p.id}>{`${p.name} - Free learning via Study Bison.`}</li>
          ))}
        </ul>
        <p>
          Remember, whenever you need free education, think of Study Bison.
          Thank you for choosing Study Bison!
        </p>
      </div>

      {/* Subtle top & center theme glow */}
      <div className="fixed top-[-10%] left-1/2 -translate-x-1/2 w-[400px] sm:w-[600px] h-[300px] sm:h-[400px] theme-glow-blob-sm rounded-full blur-[120px] pointer-events-none z-[-1]" />
      <div className="fixed top-[40%] left-1/2 -translate-x-1/2 w-[300px] sm:w-[800px] h-[400px] sm:h-[600px] theme-glow-blob-lg rounded-full blur-[150px] pointer-events-none z-[-1]" />

      {/* Admin Inbox Button (Top Right) */}
      <button
        onClick={handleInboxOpen}
        className="fixed top-5 right-5 sm:top-8 sm:right-8 z-40 bg-zinc-900/80 backdrop-blur-md border border-zinc-800 p-3 rounded-2xl shadow-lg hover:bg-zinc-800 hover:border-zinc-700 hover:shadow-xl transition-all duration-300 group"
        aria-label="Notifications"
      >
        <Bell
          className={`w-6 h-6 theme-accent-text group-hover:scale-110 transition-transform ${
            mounted && unreadCount > 0 ? "animate-bell-ring" : ""
          }`}
        />
        {mounted && unreadCount > 0 && (
          <span className="absolute -top-1.5 -right-1.5 min-w-[20px] h-[20px] bg-red-500 rounded-full items-center justify-center text-[10px] font-bold text-white shadow-[0_0_10px_rgba(239,68,68,0.9)] ring-2 ring-zinc-950 px-1 flex">
            {unreadCount}
          </span>
        )}
      </button>

      {/* Main */}
      <main className="w-full max-w-5xl mx-auto px-4 py-8 sm:py-12 flex flex-col items-center justify-center relative z-10 flex-1">
        {/* Header Section */}
        <header className="text-center mb-10 w-full flex-shrink-0 relative">
          {/* Logo */}
          <div
            className="relative w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-6 animate-pop-in"
            style={{ animationDelay: "50ms" }}
          >
            <div className="animate-levitate w-full h-full relative">
              <div className="absolute inset-[-4px] rounded-full theme-logo-glow blur-[10px]" />
              <div className="relative w-full h-full bg-zinc-950 rounded-full p-1 z-10 border border-zinc-800">
                { }
                <img
                  src="/logo.png"
                  alt="Study Bison Logo - Study Bison Ecosystem Official Logo"
                  className="w-full h-full object-cover rounded-full"
                />
              </div>
            </div>
          </div>

          {/* Welcome Title */}
<h1
              className="text-[2.5rem] sm:text-[3.5rem] font-serif-premium font-bold text-white mb-2 leading-[1.1] flex flex-col items-center justify-center animate-pop-in drop-shadow-2xl"
              style={{ fontFamily: "Playfair Display, Georgia, 'Times New Roman', serif, ui-serif, system-ui", animationDelay: "150ms" }}
            >
            <span>{settings.welcomeTitleTop}</span>
            <span className="theme-accent-text mt-1">
              {settings.welcomeTitleMain}
            </span>
          </h1>

          {/* Subtitle */}
          <p
            className="text-zinc-300 font-serif-premium text-sm sm:text-base mt-4 font-medium tracking-wide animate-pop-in"
            style={{ animationDelay: "250ms" }}
          >
            {settings.tagline.split(" ").map((word, i) =>
              word.toLowerCase() === "free" ? (
                <span key={i} className="theme-accent-text font-bold">
                  {word}{" "}
                </span>
              ) : (
                <span key={i}>{word} </span>
              )
            )}
          </p>
        </header>

        {/* Platforms Grid */}
        <nav
          className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-5 md:gap-6 w-full max-w-sm md:max-w-3xl lg:max-w-4xl mx-auto z-10"
          aria-label="Study Bison Platforms"
        >
          {platforms.map((platform, index) => (
            <a
              key={platform.id}
              href={platform.url}
              target="_blank"
              rel="noopener noreferrer"
              title={`${platform.name} on Study Bison`}
              className="group relative bg-zinc-900/60 backdrop-blur-sm border border-zinc-800/80 p-5 rounded-3xl platform-card flex flex-col items-center justify-center gap-3 cursor-pointer animate-pop-in overflow-hidden"
              style={{ animationDelay: `${500 + index * 50}ms` }}
            >
              {/* Hover accent wash */}
              <div className="absolute inset-0 bg-gradient-to-b from-[rgba(var(--sb-accent-rgb),0.08)] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />

              <div className="relative w-16 h-16 sm:w-[72px] sm:h-[72px] rounded-[1.25rem] border-2 theme-accent-border-mid bg-zinc-950 p-1 flex items-center justify-center transition-all duration-300 group-hover:scale-105 group-hover:theme-accent-border overflow-hidden shadow-[0_4px_16px_rgba(0,0,0,0.4)]">
                <img
                  src={platform.logo}
                  alt={`${platform.name} - Study Bison Platform`}
                  className="w-full h-full object-contain rounded-xl"
                  loading="lazy"
                />
              </div>
              <h2 className="relative text-white font-serif-premium font-bold text-[13px] sm:text-[15px] text-center tracking-wide group-theme-accent transition-colors">
                {platform.name}
              </h2>
            </a>
          ))}
        </nav>

        {/* Bug report — delivered to Discord via /api/bug-report */}
        <BugReportForm />
      </main>

      {/* Community Popup */}
      <CommunityPopup settings={settings} />

      {/* Inbox Panel */}
      <InboxPanel
        open={inboxOpen}
        onClose={handleInboxOpen}
        announcements={announcements}
      />

      {/* Footer */}
      <footer
        className="w-full py-4 text-center mt-6 relative z-10 flex-shrink-0 animate-pop-in"
        style={{ animationDelay: "1100ms" }}
      >
        <p className="text-zinc-600 text-[10px] font-medium tracking-widest uppercase">
          {settings.footerText}
        </p>
        <div className="seo-hidden-text">
          Study Bison | Study Bison Next Toppers | Study Bison Physics Wallah |
          Free Education Platform
        </div>
        <Link
          href="/admin"
          className="inline-block mt-1 text-zinc-700 hover:text-zinc-500 text-[10px] tracking-widest uppercase transition-colors"
        >
          Admin
        </Link>
      </footer>
    </div>
  );
}
